import React, { useEffect } from 'react';
import type { PickupLocation, ShuttleReservation } from '../../types';

export interface ShuttlePrintData {
    pickupLocations: PickupLocation[];
    reservations: ShuttleReservation[];
    selectedDate: string;
    propertyName: string;
}

interface ShuttlePrintPageProps extends ShuttlePrintData {
    onDone: () => void;
}

const ShuttlePrintPage: React.FC<ShuttlePrintPageProps> = ({ reservations, selectedDate, propertyName, onDone }) => {
    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        document.body.style.cssText = 'background-color: white;';
        
        const timer = setTimeout(() => {
            window.print();
            onDone();
        }, 500); 

        return () => {
            clearTimeout(timer);
            document.body.style.cssText = originalBodyStyle;
        };
    }, [onDone]);

    const amTimes = [
        '01:00', '02:00', '03:00', '04:00', '04:30', '05:00', '05:30',
        '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00',
        '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
        '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00'
    ];

    const getReservationsForTime = (time: string) => {
        return reservations.filter(r => r.time === time);
    };

    return (
        <div className="print-container bg-white p-4 font-sans text-black max-w-[210mm] mx-auto">
            <style type="text/css" media="print">
                {`
                @page { size: A4 portrait; margin: 0.5cm; }
                body { background: white; -webkit-print-color-adjust: exact; color-adjust: exact; }
                .print-container { width: 100%; }
                table { border-collapse: collapse; width: 100%; border: 2px solid black; }
                th, td { border: 1px solid black; padding: 2px; text-align: center; height: 28px; }
                th { background-color: #f0f0f0 !important; font-size: 12px; }
                .header-container { margin-bottom: 20px; border-bottom: 2px solid black; padding-bottom: 10px; }
                .property-name { font-size: 28px; font-weight: 900; text-transform: uppercase; color: #1e1b4b; line-height: 1; }
                .sheet-title { font-size: 18px; font-weight: 700; text-transform: uppercase; color: #4b5563; margin-top: 4px; }
                .header-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
                .date-label { font-size: 16px; font-weight: 800; }
                .max-label { font-size: 18px; font-weight: 900; border: 3px solid black; padding: 4px 12px; background: #000; color: #fff; }
                `}
            </style>

            <div className="header-container">
                <div className="flex justify-between items-start">
                    <div>
                        <div className="property-name">{propertyName}</div>
                        <div className="sheet-title">Shuttle Service Attendance Log</div>
                    </div>
                    <div className="max-label">MAX 14</div>
                </div>
                <div className="header-meta">
                    <div className="date-label">DATE: <span className="border-b-2 border-black px-4">{selectedDate}</span></div>
                    <div className="text-xs font-bold uppercase text-gray-400 italic">Official Daily Record</div>
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th className="w-[80px] text-lg font-black">A.M.</th>
                        {[...Array(14)].map((_, i) => (
                            <th key={i} className="w-[40px]"></th>
                        ))}
                        <th className="w-[50px] text-[10px] font-black uppercase">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {amTimes.map(time => {
                        const slotReservations = getReservationsForTime(time);
                        const entries: string[] = [];
                        let totalGuests = 0;
                        slotReservations.forEach(res => {
                            totalGuests += res.guests;
                            for (let i = 0; i < res.guests; i++) {
                                entries.push(res.roomNumber);
                            }
                        });

                        return (
                            <tr key={time}>
                                <td className="font-bold text-sm">{time}</td>
                                {[...Array(14)].map((_, i) => (
                                    <td key={i} className="text-[11px] font-bold">
                                        {entries[i] ? (
                                            <div className="flex justify-between items-center px-0.5">
                                                <span>{entries[i]}</span>
                                                <span className="text-sm">X</span>
                                            </div>
                                        ) : null}
                                    </td>
                                ))}
                                <td className="bg-gray-100 font-black text-sm">
                                    {totalGuests > 0 ? totalGuests : ''}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ShuttlePrintPage;
